import type { SortingState } from '@tanstack/react-table';
import { DataTable } from '../../../components/common/DataTable';
import { FilterBar, SelectFilter } from '../../../components/common/FilterBar';
import {
  formatReportingLifecycleLabel,
  formatNullableCount,
  formatNullableDate,
  type ReportingLifecycleFilterValue,
} from '../reportingUtils';
import type { ReportingApp } from '../types';
import {
  RatingDistributionBar,
  RatingStars,
  ReportingPanel,
  ReportingStatusPill,
} from './ReportingChrome';
import { ReportingAppIcon } from './ReportingAppIcon';
import { ReportingMobileStat } from './ReportingMobileStat';

interface PublishedAppsSectionProps {
  apps: ReportingApp[];
  categoryOptions: Array<{ value: string; label: string }>;
  lifecycleOptions: Array<{
    value: ReportingLifecycleFilterValue;
    label: string;
  }>;
  searchValue: string;
  categoryValue: string;
  lifecycleValue: ReportingLifecycleFilterValue;
  sorting: SortingState;
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onLifecycleChange: (value: ReportingLifecycleFilterValue) => void;
  onSortingChange: (sorting: SortingState) => void;
  onAppFocus?: (app: ReportingApp) => void;
}

export function PublishedAppsSection({
  apps,
  categoryOptions,
  lifecycleOptions,
  searchValue,
  categoryValue,
  lifecycleValue,
  sorting,
  onSearchChange,
  onCategoryChange,
  onLifecycleChange,
  onSortingChange,
  onAppFocus,
}: PublishedAppsSectionProps) {
  return (
    <ReportingPanel>
      <div className="space-y-4 p-5">
        <FilterBar
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder="Search apps, packages, or publishers..."
          className="rounded-lg border-neutral-800/70 bg-neutral-950/40 p-2"
        >
          <SelectFilter
            value={categoryValue}
            onChange={onCategoryChange}
            options={categoryOptions}
            className="rounded-md border-neutral-700/60 bg-neutral-900/60"
          />
          <SelectFilter
            value={lifecycleValue}
            onChange={(value) =>
              onLifecycleChange(value as ReportingLifecycleFilterValue)
            }
            options={lifecycleOptions}
            className="rounded-md border-neutral-700/60 bg-neutral-900/60"
          />
          <span className="ml-auto text-xs tabular-nums text-neutral-500">
            {apps.length.toLocaleString()} apps
          </span>
        </FilterBar>

        <div className="overflow-hidden rounded-lg border border-neutral-800/70">
          <DataTable
            data={apps}
            enableSorting
            sorting={sorting}
            onSortingChange={(nextSorting) => {
              const resolved =
                typeof nextSorting === 'function'
                  ? nextSorting(sorting)
                  : nextSorting;
              onSortingChange(resolved);
            }}
            getRowId={(row) => row.dappId}
            onRowClick={onAppFocus}
            emptyMessage="No apps match the current filters"
            columns={[
              {
                key: 'dappName',
                header: 'App',
                accessor: (row) => <AppCell app={row} />,
                size: 320,
              },
              {
                key: 'category',
                header: 'Category',
                accessor: (row) => (
                  <span className="text-sm text-neutral-300">
                    {row.storePrimaryCategory ?? row.category}
                  </span>
                ),
              },
              {
                key: 'lifecycle',
                header: 'Activity',
                accessor: (row) => (
                  <ReportingStatusPill
                    label={formatReportingLifecycleLabel(row.lifecycle)}
                    tone={row.lifecycle === 'new' ? 'teal' : 'neutral'}
                  />
                ),
              },
              {
                key: 'storeVersionCode',
                header: 'Version',
                accessor: (row) => (
                  <div className="text-sm text-neutral-300">
                    {row.storeListing?.versionName ? (
                      <>
                        <span className="font-medium tabular-nums text-white">
                          v{row.storeListing.versionName}
                        </span>
                        {row.storeListing.versionCode !== null && (
                          <span className="ml-1 text-xs text-neutral-500">
                            ({row.storeListing.versionCode})
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-neutral-500">{'—'}</span>
                    )}
                  </div>
                ),
              },
              {
                key: 'averageReviewScore',
                header: 'Rating',
                accessor: (row) => (
                  <div className="flex flex-col gap-1">
                    {row.reviews !== null && row.reviews > 0 ? (
                      <>
                        <RatingStars
                          value={row.averageReviewScore}
                          showValue
                          size="sm"
                        />
                        <RatingDistributionBar
                          buckets={{
                            one: row.oneStarReviews,
                            two: row.twoStarReviews,
                            three: row.threeStarReviews,
                            four: row.fourStarReviews,
                            five: row.fiveStarReviews,
                          }}
                          variant="compact"
                          className="w-32"
                        />
                      </>
                    ) : (
                      <span className="text-xs text-neutral-500">
                        {row.reviews === null
                          ? row.packageName
                            ? '—'
                            : 'No package'
                          : 'No ratings'}
                      </span>
                    )}
                  </div>
                ),
                sortingFn: (left, right) => {
                  const leftScore = left.averageReviewScore ?? -1;
                  const rightScore = right.averageReviewScore ?? -1;
                  if (leftScore === rightScore) {
                    return (left.reviews ?? 0) - (right.reviews ?? 0);
                  }
                  return leftScore - rightScore;
                },
              },
              {
                key: 'reviews',
                header: 'Lifetime reviews',
                accessor: (row) => (
                  <span className="text-sm tabular-nums text-neutral-300">
                    {formatNullableCount(
                      row.reviews,
                      row.packageName ? '—' : 'No pkg',
                    )}
                  </span>
                ),
              },
              {
                key: 'updatesInPeriod',
                header: 'Updates',
                accessor: (row) => (
                  <span className="text-sm tabular-nums text-neutral-300">
                    {row.updatesInPeriod.toLocaleString()}
                  </span>
                ),
              },
              {
                key: 'latestPublishedAtInRange',
                header: 'Last Published',
                accessor: (row) => (
                  <span className="text-sm text-neutral-400">
                    {formatNullableDate(row.latestPublishedAtInRange, '—')}
                  </span>
                ),
              },
            ]}
            renderMobileItem={(row) => (
              <button
                type="button"
                onClick={onAppFocus ? () => onAppFocus(row) : undefined}
                className="flex w-full flex-col gap-3 text-left"
              >
                <div className="flex items-start gap-3">
                  <ReportingAppIcon
                    app={row}
                    size="md"
                    className="border-neutral-800/40"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-white">
                      {row.dappName}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-neutral-500">
                      {row.storePrimaryCategory ?? row.category}
                      {row.packageName ? ` · ${row.packageName}` : ''}
                    </div>
                  </div>
                  <ReportingStatusPill
                    label={formatReportingLifecycleLabel(row.lifecycle)}
                    tone={row.lifecycle === 'new' ? 'teal' : 'neutral'}
                  />
                </div>

                {row.reviews !== null && row.reviews > 0 && (
                  <div className="flex items-center justify-between gap-3">
                    <RatingStars
                      value={row.averageReviewScore}
                      showValue
                      size="sm"
                    />
                    <span className="text-xs tabular-nums text-neutral-500">
                      {row.reviews.toLocaleString()} lifetime reviews
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-2 text-xs">
                  <ReportingMobileStat
                    label="Updates"
                    value={row.updatesInPeriod.toLocaleString()}
                  />
                  <ReportingMobileStat
                    label="Version"
                    value={
                      row.storeListing?.versionName
                        ? `v${row.storeListing.versionName}`
                        : '—'
                    }
                  />
                  <ReportingMobileStat
                    label="Published"
                    value={formatNullableDate(
                      row.latestPublishedAtInRange,
                      '—',
                    )}
                  />
                </div>

                {row.reviews !== null && row.reviews > 0 && (
                  <RatingDistributionBar
                    buckets={{
                      one: row.oneStarReviews,
                      two: row.twoStarReviews,
                      three: row.threeStarReviews,
                      four: row.fourStarReviews,
                      five: row.fiveStarReviews,
                    }}
                    variant="compact"
                  />
                )}
              </button>
            )}
            tableClassName="min-w-[1100px]"
            theadClassName="bg-neutral-950/30"
            headerRowClassName="border-neutral-800/40"
            rowBaseClassName="border-b border-neutral-800/30 transition-colors hover:bg-white/[0.02] cursor-pointer"
          />
        </div>
      </div>
    </ReportingPanel>
  );
}

function AppCell({ app }: { app: ReportingApp }) {
  return (
    <div className="flex items-center gap-3">
      <ReportingAppIcon app={app} />
      <div className="min-w-0">
        <div className="truncate text-sm text-neutral-100">{app.dappName}</div>
        <div className="truncate text-xs text-neutral-500">
          {app.packageName || 'No package'}
          {app.storeListing?.publisherName
            ? ` · ${app.storeListing.publisherName}`
            : ''}
        </div>
      </div>
    </div>
  );
}
